import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, matchPath, useMatch } from 'react-router';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { Mixpanel } from 'Utils/MixPanel';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { MinhaContaActions } from 'Store/MinhaConta/MinhaConta.actions';
import { getCurrentTenant } from 'Store/Tenant/Tenant.selector';
import Button from '../../UI/Button';
import { Icon } from '../../UI/Icon';
import Profile from './Profile/Profile';
import CompletarCadastro from './CompletarCadastro/CompletarCadastro';
import NotificationList from './NotificationList';
import UpgradePremium from './Modals/UpgradePremium';
import AvisoUpgradeConfirmacao from './Modals/AvisoUpgradeConfirmacao';
import { useQueryCountNewNotifications } from 'Hooks/notifications/useQueryCountNewNotifications';
import { useQueryCountNewNotificationsObra } from 'Hooks/notifications/useQueryCountNewNotificationsObra';
import { Divider, NavContainer } from './styles';

export interface ITopBar {
  onCollapseClick: () => void;
  isCollapsed: boolean;
}

const TopBar: FC<Props> = ({
  showModalUpdatePremium,
  showModalConfirmUpdatePremium,
  onCollapseClick,
  isCollapsed,
  isLoading,
  newNotifications,
  planFree,
  userInfo: userInforGet,
  userLoading,
  updateEmpresaLoading,
  tenants,
}) => {
  const { t } = useTranslation('sharedModals');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const routesPath = [
    '/obras/detail/:constructionSiteId',
    '/obras/detail/:constructionSiteId/:isNewConstructionSite',
    '/obras/filelist/:constructionSiteId/:folderId',
    '/obras/plotagens/:constructionSiteId',
    '/obras/plotagens/filelist/:constructionSiteId/:ConstructionSiteDisciplineFk',
    '/obras/plotagens/filelist/receber/:constructionSiteId/:ConstructionSiteDisciplineFk',
    '/obras/viewer/detail/:constructionSiteId',
    '/obras/activities/detail/:constructionSiteId',
    '/obras/panels/:constructionSiteId/:panelId',
    '/obras/edit/:constructionSiteId/:editId',
  ];
  const activePath = routesPath.find((pathPattern) =>
    matchPath(pathPattern, location.pathname),
  ) || '';
  const match = useMatch({ path: activePath });
  const currentTenant = useSelector(getCurrentTenant);
  const constructionSiteId = match?.params?.constructionSiteId
    ? Number(match.params.constructionSiteId)
    : null;

  useQueryCountNewNotifications();
  useQueryCountNewNotificationsObra(constructionSiteId);

  const openUpgradePremium = () => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'top-bar-upgrade-button',
        originPath: window.location.pathname,
      },
      userInfo: userInforGet,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? (tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[])
        : undefined,
    });

    if (userInforGet?.CurrentRoleFk === 1) {
      navigate('/faturamento/planos');
    } else {
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  };

  const closeUpgradePremium = () => {
    dispatch(MinhaContaActions.setShowModalUpdatePremium(false));
  };

  return (
    <>
      <NavContainer iscollapsed={isCollapsed}>
        <div className='left'>
          <div
            className='buttonCollapse'
            onClick={onCollapseClick}
          >
            <Icon icon='collapse' color='cinzaSuperior' customSize={12} />
          </div>

          {(!userInforGet?.isRegisterComplete && userInforGet?.CurrentRoleFk === 1) && (
            <CompletarCadastro
              icon='informacao'
              title={t('common.completeRegistration')}
              isLoading={isLoading || userLoading}
              userInfo={userInforGet}
              updateEmpresaLoading={updateEmpresaLoading}
            />
          )}
        </div>

        <aside>
          {planFree && !isLoading && (
            <Button
              type='primary'
              ispremium='true'
              onClick={() => openUpgradePremium()}
              className='buttonPremium'
            >
              <div>
                <Icon
                  icon='crown'
                  size='xxs'
                  className='crownPremium'
                />
                <span>Upgrade</span>
              </div>
            </Button>
          )}
          <NotificationList
            constructionSiteId={constructionSiteId}
            loadingExternalLink={false}
          />
          <Divider />

          <Profile
            name={userInforGet?.Nome || ''}
            image={userInforGet?.OriginalImageUrl || ''}
            thumb={userInforGet?.ThumbnailImageUrl}
            email={userInforGet?.Email || ''}
            hash={userInforGet?.UserHash || ''}
            userId={userInforGet?.Id || ''}
            isFreemium={planFree || true}
            tenantId={userInforGet?.CurrentTenantContext || 0}
            tenantUserRole={userInforGet?.CurrentRoleFk || 0}
            isLoading={isLoading}
            currentRole={userInforGet?.CurrentRoleFk}
            isSP={userInforGet?.isSP}
          />
        </aside>
      </NavContainer>

      {showModalUpdatePremium && (
        <UpgradePremium
          visible={showModalUpdatePremium}
          onCancel={closeUpgradePremium}
        />
      )}
      {showModalConfirmUpdatePremium && (
        <AvisoUpgradeConfirmacao
          visible={showModalConfirmUpdatePremium}
          onConfirm={() => dispatch(MinhaContaActions.setShowModalConfirmPremium(false))}
        />
      )}
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.auth,
  showModalConfirmUpdatePremium: state.minhaConta.showModalConfirmUpdatePremium,
  showModalUpdatePremium: state.minhaConta.showModalUpdatePremium,
  registerCompleted: state.minhaConta.userInfo?.isRegisterComplete,
  newNotifications: state.notification.newNotifications,
  userLoading: state.auth.isLoading,
  updateEmpresaLoading: state.minhaConta.isLoading,
  tenants: state.tenant.Tenants,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & ITopBar;

export default connector(TopBar);
